/**
 * Navbar - MarIA Health Care
 *
 * Responsive navigation bar with mobile-first design.
 *
 * Features:
 * - Mobile: Icon-only buttons to save space
 * - Desktop: Full text labels
 * - Accessible: aria-labels on all interactive elements
 * - Sticky header with blur backdrop
 *
 * @see Issue #XX - Navbar Responsive Fix
 */
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Heart/Health icon for profile button
 */
const HeartIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
    </svg>
);

/**
 * Chat bubble icon for chat button
 */
const ChatIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97z" clipRule="evenodd" />
    </svg>
);

/**
 * Logout/exit icon
 */
const LogoutIcon = ({ className }: { className?: string }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className={className}
        aria-hidden="true"
    >
        <path fillRule="evenodd" d="M7.5 3.75A1.5 1.5 0 006 5.25v13.5a1.5 1.5 0 001.5 1.5h6a1.5 1.5 0 001.5-1.5V15a.75.75 0 011.5 0v3.75a3 3 0 01-3 3h-6a3 3 0 01-3-3V5.25a3 3 0 013-3h6a3 3 0 013 3V9A.75.75 0 0115 9V5.25a1.5 1.5 0 00-1.5-1.5h-6zm10.72 4.72a.75.75 0 011.06 0l3 3a.75.75 0 010 1.06l-3 3a.75.75 0 11-1.06-1.06l1.72-1.72H9a.75.75 0 010-1.5h10.94l-1.72-1.72a.75.75 0 010-1.06z" clipRule="evenodd" />
    </svg>
);

export const Navbar = () => {
    const { isAuthenticated, logout, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isChat = location.pathname === "/chat";
    const isSurvey = location.pathname === "/mi-salud";

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="bg-white/95 backdrop-blur-sm border-b border-border shadow-sm sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="flex justify-between h-14 sm:h-16 items-center">
                    {/* Logo - shorter on mobile */}
                    <div
                        className="flex-shrink-0 flex items-center cursor-pointer"
                        onClick={() => navigate('/')}
                        role="button"
                        tabIndex={0}
                        aria-label="Ir a inicio"
                        onKeyDown={(e) => e.key === 'Enter' && navigate('/')}
                    >
                        <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-primary mr-2"></div>
                        <span className="font-bold text-lg sm:text-xl bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                            {/* Short name on mobile, full on desktop */}
                            <span className="sm:hidden">MarIA</span>
                            <span className="hidden sm:inline">MarIA Health Care</span>
                        </span>
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex items-center gap-1 sm:gap-2">
                        {isAuthenticated ? (
                            <>
                                {/* User name - only on larger screens */}
                                {user?.displayName && (
                                    <span className="hidden lg:inline text-sm font-medium text-muted-foreground mr-2">
                                        {user.displayName}
                                    </span>
                                )}

                                {/* "Editar mi perfil" button when on chat page */}
                                {isChat && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-primary border-primary/20 hover:bg-primary/5"
                                        onClick={() => navigate('/mi-salud')}
                                        aria-label="Editar tu perfil de salud"
                                    >
                                        <HeartIcon className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Editar mi perfil</span>
                                    </Button>
                                )}

                                {/* "Chat" button when on survey page */}
                                {isSurvey && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="text-primary border-primary/20 hover:bg-primary/5"
                                        onClick={() => navigate('/chat')}
                                        aria-label="Volver al chat con MarIA"
                                    >
                                        <ChatIcon className="h-4 w-4 sm:mr-2" />
                                        <span className="hidden sm:inline">Chat</span>
                                    </Button>
                                )}

                                {/* Logout button */}
                                <Button
                                    onClick={handleLogout}
                                    variant="ghost"
                                    size="sm"
                                    aria-label="Cerrar sesión"
                                    className="text-muted-foreground hover:text-foreground"
                                >
                                    <LogoutIcon className="h-4 w-4 sm:mr-2" />
                                    <span className="hidden sm:inline">Salir</span>
                                </Button>
                            </>
                        ) : (
                            <span className="text-sm text-muted-foreground">Bienvenida</span>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};